<template>
  <div class="datepicker-root d-flex flex-column bg-white shadow-lg border rounded overflow-hidden">
    <div class="d-flex">
      <div class="calendar-sidebar border-end" style="width: 190px; background-color: #fff; flex-shrink: 0;">
        <div 
          v-for="option in quickOptions" 
          :key="option.key"
          class="quick-option" 
          :class="{ 'active-preset': activePreset === option.key && !isCustomActive }"
          @click="selectQuickRange(option.key)"
        >
          {{ option.label }}
        </div>
        <div 
          class="quick-option border-top" 
          :class="{ 'active-preset': isCustomActive }"
          @click="selectCustomRange()"
        >
          Custom Range
        </div>
      </div>

      <div v-if="isCustomActive" class="calendar-main-content p-3 bg-white" style="min-width: 580px;">
        <div class="d-flex gap-4">
          <div
            v-for="(month, mIdx) in monthsData"
            :key="mIdx"
            class="calendar-month"
            style="width: 260px;"
          >
            <div class="d-flex justify-content-between align-items-center mb-3 px-1">
              <span v-if="mIdx === 0" @click="movePrevMonth()" class="nav-btn">&lt;</span>
              <span v-else></span>
              
              <span class="fw-bold text-dark text-capitalize">{{ month.monthName }}</span>
              
              <span v-if="mIdx === 1" @click="moveNextMonth()" class="nav-btn">&gt;</span>
              <span v-else></span>
            </div>

            <div class="d-flex border-bottom pb-1 mb-2">
              <span v-for="day in daysName" :key="day" class="weekday-label">{{ day }}</span>
            </div>

            <div class="calendar-grid">
              <div 
                v-for="(dt, dIdx) in month.dates" 
                :key="dIdx"
                class="date-wrapper"
              >
                <div 
                  v-if="dt"
                  class="date-cell"
                  :class="getDateClasses(dt)"
                  @click="onSelectDate(dt)"
                  @mouseover="hoverDate(dt)"
                >
                  {{ dt.dateNumber }}
                </div>
                <div v-else class="blank-cell"></div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div v-if="isCustomActive" class="border-top p-2 d-flex justify-content-between align-items-center bg-light">
      <div class="ps-3 small text-muted font-monospace">
        {{ formatDisplayDate(selected.start_date) }} - {{ formatDisplayDate(selected.end_date) }}
      </div>
      <div class="pe-2">
        <button class="btn btn-link btn-sm text-dark text-decoration-none fw-bold me-3" @click="isCustomActive = false">Cancel</button>
        <button class="btn btn-primary btn-sm px-4 fw-bold" style="background-color: #2c3e8c; border: none;" @click="clickApply">Apply</button>
      </div>
    </div>
  </div>
</template>

<script>
import moment from "moment";
import "moment/locale/fr";
moment.locale("fr");

export default {
  name: "MyDateRangePicker",
  props: {
    modelValue: { type: Array, default: () => [] }
  },
  data() {
    return {
      current_view_date: moment().startOf('month'),
      selected: { start_date: null, end_date: null },
      start_date_selected: false,
      isCustomActive: false,
      activePreset: 'sinceLaunch',
      daysName: ["Di", "Lu", "Ma", "Me", "Je", "Ve", "Sa"],
      quickOptions: [
        { label: "Depuis le lancement", key: "sinceLaunch" },
        { label: "Aujourd'hui", key: "today" },
        { label: "Hier", key: "yesterday" },
        { label: "7 derniers jours", key: "last7days" },
        { label: "30 derniers jours", key: "last30days" },
        { label: "Ce mois", key: "thisMonth" },
        { label: "Le mois dernier", key: "lastMonth" },
      ]
    };
  },
  computed: {
    monthsData() {
      return [
        this.generateMonth(this.current_view_date.clone().subtract(1, 'M')),
        this.generateMonth(this.current_view_date.clone())
      ];
    }
  },
  mounted() {
    // Initialize with "Since Launch" by default to match your UI
    this.selectQuickRange('sinceLaunch');
  },
  methods: {
    generateMonth(mDate) {
      const startOfMonth = mDate.clone().startOf('month');
      const endOfMonth = mDate.clone().endOf('month');
      const dates = [];
      for (let i = 0; i < startOfMonth.day(); i++) dates.push(null);
      let day = startOfMonth.clone();
      while (day.isSameOrBefore(endOfMonth, 'day')) {
        dates.push({ date: day.clone(), dateNumber: day.date(), isToday: moment().isSame(day, 'day') });
        day.add(1, 'day');
      }
      return { monthName: mDate.format("MMMM YYYY"), dates: dates };
    },
    getDateClasses(dt) {
      return {
        'date-selected': this.selected.start_date?.isSame(dt.date, 'day') || this.selected.end_date?.isSame(dt.date, 'day'),
        'date-in-range': this.isDateInRange(dt.date),
        'date-start': this.selected.start_date?.isSame(dt.date, 'day'),
        'date-end': this.selected.end_date?.isSame(dt.date, 'day'),
        'date-today': dt.isToday
      };
    },
    isDateInRange(date) {
      if (!this.selected.start_date || !this.selected.end_date) return false;
      return date.isBetween(this.selected.start_date, this.selected.end_date, 'day', '[]');
    },
    onSelectDate(dt) {
      if (!this.start_date_selected) {
        this.selected.start_date = dt.date;
        this.selected.end_date = null;
        this.start_date_selected = true;
      } else {
        if (dt.date.isBefore(this.selected.start_date)) {
          this.selected.end_date = this.selected.start_date;
          this.selected.start_date = dt.date;
        } else {
          this.selected.end_date = dt.date;
        }
        this.start_date_selected = false;
      }
    },
    hoverDate(dt) {
      if (this.start_date_selected) this.selected.end_date = dt.date;
    },
    selectQuickRange(key) {
      this.isCustomActive = false;
      this.activePreset = key;
      const today = moment().startOf('day');
      let start = today.clone(), end = today.clone();

      if (key === 'sinceLaunch') start = moment("2022-09-30");
      else if (key === 'yesterday') { start = today.clone().subtract(1, 'd'); end = start.clone(); }
      else if (key === 'last7days') start = today.clone().subtract(6, 'd');
      else if (key === 'last30days') start = today.clone().subtract(29, 'd');
      else if (key === 'thisMonth') start = today.clone().startOf('month');
      else if (key === 'lastMonth') { 
        start = today.clone().subtract(1, 'M').startOf('month'); 
        end = today.clone().subtract(1, 'M').endOf('month'); 
      }

      this.selected.start_date = start;
      this.selected.end_date = end;
      this.emitChange();
    },
    selectCustomRange() {
      this.isCustomActive = true;
      this.activePreset = 'custom';
    },
    formatDisplayDate(d) { return d ? d.format("MM/DD/YYYY") : '--/--/----'; },
    moveNextMonth() { this.current_view_date = this.current_view_date.add(1, 'M').clone(); },
    movePrevMonth() { this.current_view_date = this.current_view_date.subtract(1, 'M').clone(); },
    clickApply() { this.emitChange(); this.isCustomActive = false; },
    emitChange() {
      const range = [this.selected.start_date, this.selected.end_date];
      this.$emit('update:modelValue', range);
      this.$emit('onSelect', range);
    }
  }
};
</script>

<style lang="scss" scoped>
.quick-option {
  padding: 10px 16px;
  cursor: pointer;
  font-size: 13.5px;
  color: #6a71fe;
  transition: all 0.2s;
  &:hover { background-color: #f8f9fa; }
  &.active-preset {
    background-color: #008ecb !important;
    color: white !important;
  }
}

.calendar-grid { display: flex; flex-wrap: wrap; width: 100%; }
.date-wrapper { width: 14.28%; display: flex; justify-content: center; margin-bottom: 2px; }
.date-cell {
  width: 32px; height: 32px; display: flex; align-items: center; justify-content: center;
  cursor: pointer; font-size: 12px; border-radius: 4px;
  &:hover { background-color: #008ecb; color: #fff; }
  &.date-in-range { background-color: #eef1ff; border-radius: 0; }
  &.date-selected { background-color: #008ecb; color: white; }
  &.date-start { border-top-right-radius: 0; border-bottom-right-radius: 0; background-color: #2c3e8c !important; }
  &.date-end { border-top-left-radius: 0; border-bottom-left-radius: 0; background-color: #2c3e8c !important; }
  &.date-today { border: 1px solid #008ecb; font-weight: bold; }
}
.blank-cell { width: 32px; height: 32px; }
.nav-btn { cursor: pointer; padding: 0 10px; font-weight: bold; &:hover { color: #008ecb; } }
.weekday-label { width: 14.28%; text-align: center; font-size: 11px; font-weight: bold; color: #adb5bd; }
</style>